import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import Analytics from '../components/Template/Analytics';
import Navigation from '../components/Template/Navigation';
import SideBar from '../components/Template/SideBar';
import ScrollToTop from '../components/Template/ScrollToTop';
import {
  SITE_URLS,
  SEO_METADATA,
  SOCIAL_LINKS,
  PERSONAL_INFO,
} from '../constants';

interface MainProps {
  children?: React.ReactNode;
  fullPage?: boolean;
  title?: string;
  description?: string;
  image?: string;
  article?: boolean;
}

const Main: React.FC<MainProps> = ({
  children,
  fullPage = false,
  title,
  description = SEO_METADATA.DEFAULT_DESCRIPTION,
  image = SEO_METADATA.DEFAULT_IMAGE,
  article = false,
}) => {
  const location = useLocation();
  const currentUrl = `${SITE_URLS.BASE_URL}${location.pathname}`;
  const fullImageUrl = `${SITE_URLS.BASE_URL}${image}`;
  const pageTitle = title
    ? `${title} | ${SEO_METADATA.DEFAULT_TITLE}`
    : `${SEO_METADATA.DEFAULT_TITLE} - ${PERSONAL_INFO.TITLE} at ${PERSONAL_INFO.COMPANY}`;

  // Structured data for Person schema
  const structuredData = {
    '@context': SITE_URLS.SCHEMA_ORG,
    '@type': 'Person',
    name: PERSONAL_INFO.NAME,
    alternateName: 'Alex D. Brinker',
    url: SITE_URLS.BASE_URL,
    image: `${SITE_URLS.BASE_URL}${SEO_METADATA.DEFAULT_IMAGE}`,
    sameAs: [SOCIAL_LINKS.LINKEDIN, SOCIAL_LINKS.GITHUB, SOCIAL_LINKS.TWITTER],
    jobTitle: PERSONAL_INFO.TITLE,
    worksFor: {
      '@type': 'Organization',
      name: PERSONAL_INFO.COMPANY,
    },
    alumniOf: {
      '@type': 'EducationOrganization',
      name: 'University Name',
    },
    knowsAbout: [
      'Test Automation',
      'API Testing',
      '.NET Development',
      'AI in Testing',
      'Quality Engineering',
      'Software Testing',
      'DevOps',
      'CI/CD',
    ],
    description,
    address: {
      '@type': 'PostalAddress',
      addressLocality: PERSONAL_INFO.LOCATION.split(', ')[0],
      addressRegion: PERSONAL_INFO.LOCATION.split(', ')[1],
      addressCountry: 'US',
    },
  };

  return (
    <HelmetProvider>
      <Analytics />
      <ScrollToTop />
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={description} />
        <meta name="author" content={PERSONAL_INFO.NAME} />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content={article ? 'article' : 'website'} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={fullImageUrl} />
        <meta property="og:site_name" content={SEO_METADATA.DEFAULT_TITLE} />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={currentUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={fullImageUrl} />
        <meta property="twitter:site" content={SEO_METADATA.TWITTER_HANDLE} />
        <meta
          property="twitter:creator"
          content={SEO_METADATA.TWITTER_HANDLE}
        />

        {/* Additional SEO tags */}
        <meta name="robots" content="index, follow" />
        <meta
          name="googlebot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta
          name="bingbot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div id="wrapper">
        <Navigation />
        <div id="main">{children}</div>
        {fullPage ? null : <SideBar />}
      </div>
    </HelmetProvider>
  );
};

export default Main;
